/* eslint-disable import/prefer-default-export */
import { graphql } from "gatsby";
import { getBusinessUnitKeyFromName } from "../../enums/EnumsBusinessUnits";

/**
 * Function
 *
 * Accepts a pageData object and returns an object with the properties that are specified.
 * If a property of pageData is not defined, the property of the returned object will be
 * assigned a default value.
 *
 * @param {object} pageData
 * @returns object
 */
export const getPageProperties = (pageData) => {
  const title = pageData?.title || null;
  const description = pageData?.description || null;
  const robots = pageData?.robots || null;
  const canonical = pageData?.canonical || null;
  const seoKeywords = pageData?.seoKeywords || null;
  const previewImage = pageData?.previewImage?.file?.url;
  const schema = pageData?.schema || null;
  const notificationBanner = pageData?.notificationBanner || null;
  const showBanner = pageData?.showBanner ?? null;
  const isTrialPage = pageData?.showBanner || false;
  const sourceQueryParam = pageData?.sourceQueryParam || null;
  const secondaryNavigation = pageData?.secondaryNavigation || null;
  const breadcrumbName = pageData?.breadcrumbName || null;
  const breadcrumbTrail = pageData?.breadcrumbTrail || null;
  const mainNavigation = pageData?.mainNavigation || null;
  const mainNavigationCtaText = pageData?.mainNavigationCtaText || null;
  const mainNavigationCtaLink = pageData?.mainNavigationCtaLink || null;
  const mainNavigationCtaType = pageData?.mainNavigationCtaType || null;
  const mainNavigationCtaPopupForm =
    pageData?.mainNavigationCtaPopupForm || null;
  const footerTheme = pageData?.footerTheme || null;
  const footerFormType = pageData?.footerFormType || "Later Social";
  const metadata = pageData?.metadata || null;
  const slug = pageData?.slug || null;
  const template = pageData?.template || null;
  const categories = pageData?.categories || null;
  const breadcrumbSchemaOnly = pageData?.breadcrumbSchemaOnly || false;

  let businessUnit = pageData?.businessUnit || "SMM";
  businessUnit = getBusinessUnitKeyFromName(businessUnit);

  const previewImageUrl = pageData?.previewImage?.file?.url
    ? `https:${pageData?.previewImage?.file?.url}`
    : null; // this will default to the image declared in SEO component

  return {
    title,
    description,
    robots,
    canonical,
    seoKeywords,
    previewImage,
    previewImageUrl,
    schema,
    notificationBanner,
    showBanner,
    isTrialPage,
    sourceQueryParam,
    secondaryNavigation,
    breadcrumbName,
    breadcrumbTrail,
    breadcrumbSchemaOnly,
    mainNavigation,
    mainNavigationCtaText,
    mainNavigationCtaLink,
    mainNavigationCtaType,
    mainNavigationCtaPopupForm,
    footerTheme,
    footerFormType,
    metadata,
    slug,
    businessUnit,
    template,
    categories,
  };
};

export const query = graphql`
  fragment PageCard on ContentfulPage {
    slug
    pageTitle: cardTitle
    bodyText: cardShortDescription {
      cardShortDescription
    }
    thumbnailImage: cardThumbnailImage {
      contentful_id
      gatsbyImageData
      description
    }
    featuredThumbnailImage {
      gatsbyImageData
      description
    }
  }

  fragment Page on ContentfulPage {
    contentful_id
    createdAt
    publishDate
    description
    title
    slug
    internalOrExternal
    businessUnit
    node_locale
    canonical
    seoKeywords
    robots
    sourceQueryParam
    isTrialPage
    breadcrumbName
    breadcrumbTrail {
      name: breadcrumbName
      url: slug
    }
    breadcrumbSchemaOnly
    ...NavigationMain
    secondaryNavigation {
      ...NavigationSecondary
    }
    previewImage {
      file {
        url
      }
    }
    schema {
      internal {
        content
      }
    }
    notificationBanner {
      ...NotificationBanner
    }
    showBanner
    categories {
      name
    }

    metadata {
      tags {
        name
        contentful_id
      }
    }
    ...FooterMain
    cardTitle
    cardShortDescription {
      cardShortDescription
    }
    cardThumbnailImage {
      gatsbyImageData
      description
    }
  }
`;
